import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MarathiEditContext = createContext(null);

export function MarathiEditProvider({ children }) {
  const { user } = useAuth();
  const [overrides, setOverrides] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/marathi-overrides`, {
          credentials: "include",
        });
        if (r.ok) setOverrides(await r.json());
      } catch { /* ignore */ }
      setReady(true);
    })();
  }, [user]);

  const updateOverride = useCallback(async (key, value) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
    try {
      await fetch(`${BACKEND_URL}/api/marathi-overrides`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ key, value }),
      });
    } catch { /* ignore */ }
  }, []);

  const bulkUpdateOverrides = useCallback(async (map) => {
    if (!map || typeof map !== "object") return;
    const filtered = Object.fromEntries(
      Object.entries(map).filter(([k]) => k && String(k).trim()),
    );
    if (!Object.keys(filtered).length) return;
    setOverrides((prev) => ({ ...prev, ...filtered }));
    try {
      await fetch(`${BACKEND_URL}/api/marathi-overrides/bulk`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ overrides: filtered }),
      });
    } catch { /* ignore */ }
  }, []);

  const requestTranslations = useCallback(async (texts) => {
    // Only ask for texts we don't already have
    const missing = texts.filter(
      (t) => t && String(t).trim() && overrides[`trans:${String(t).trim()}`] === undefined,
    );
    if (missing.length === 0) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/translate-marathi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ texts: missing }),
      });
      if (!r.ok) return;
      const map = await r.json(); // { eng: marathi }
      setOverrides((prev) => {
        const next = { ...prev };
        for (const [eng, mar] of Object.entries(map)) {
          if (mar) next[`trans:${eng}`] = mar;
        }
        return next;
      });
    } catch { /* ignore */ }
  }, [overrides]);

  return (
    <MarathiEditContext.Provider
      value={{
        overrides,
        editMode,
        ready,
        setEditMode,
        toggleEdit: () => setEditMode((v) => !v),
        updateOverride,
        bulkUpdateOverrides,
        requestTranslations,
      }}
    >
      {children}
    </MarathiEditContext.Provider>
  );
}

export const useMarathiEdit = () => useContext(MarathiEditContext);

/**
 * Translate a DYNAMIC user-entered value into its saved Marathi form.
 *  - Key format: `trans:<English source>`. So the same English value entered
 *    for the same advocate always resolves to the same Marathi translation.
 *  - When no translation is saved yet, the English source is shown as-is.
 *  - In edit mode: the span is contentEditable; on blur the corrected
 *    Marathi text is persisted per-user against the English source key.
 *  - The FIXED Marathi legal template text around this span is NOT editable.
 */
export function T({ source, placeholder = "____________" }) {
  const ctx = useContext(MarathiEditContext);
  const rawSrc = source == null ? "" : String(source);
  const src = rawSrc.trim();

  // Empty source → just show the placeholder (not editable — nothing to translate)
  if (!src) return <>{placeholder}</>;

  if (!ctx) return <>{rawSrc}</>;
  const key = `trans:${src}`;
  const val = ctx.overrides[key] !== undefined ? ctx.overrides[key] : rawSrc;

  if (!ctx.editMode) return <>{val}</>;

  return (
    <span
      contentEditable
      suppressContentEditableWarning
      className="editable-mr"
      data-mr-key={key}
      onBlur={(e) => {
        const t = e.currentTarget.innerText;
        if (t !== val) ctx.updateOverride(key, t);
      }}
    >
      {val}
    </span>
  );
}
